import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { SupabaseService } from '../../services/supabaseService'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiChevronDown, FiCheck, FiLoader } = FiIcons

const StatusDropdown = ({ user, searchId, resultId, initialStatus = 'new', onStatusChange }) => {
  const [status, setStatus] = useState(initialStatus)
  const [isOpen, setIsOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [errorTimeout, setErrorTimeout] = useState(null)

  // This ensures we update our local state when the prop changes
  useEffect(() => {
    setStatus(initialStatus)
  }, [initialStatus])

  // Clear error message after 5 seconds
  useEffect(() => {
    if (error && !errorTimeout) {
      const timeout = setTimeout(() => {
        setError(null);
        setErrorTimeout(null);
      }, 5000);
      setErrorTimeout(timeout);
      return () => {
        clearTimeout(timeout);
        setErrorTimeout(null);
      };
    }
  }, [error, errorTimeout]);

  const statusOptions = [
    { value: 'new', label: 'New', color: 'green-btn text-white' },
    { value: 'submitted', label: 'Submitted', color: 'bg-orange-500/70 text-white' },
    { value: 'live', label: 'Live', color: 'bg-yellow-500/70 text-white' }
  ]

  const getStatusColor = (statusValue) => {
    const option = statusOptions.find(opt => opt.value === statusValue)
    return option?.color || 'bg-gray-500 text-white'
  }

  const getStatusLabel = (statusValue) => {
    const option = statusOptions.find(opt => opt.value === statusValue)
    return option?.label || 'Unknown'
  }

  const toggleDropdown = () => {
    if (!isSaving) {
      setIsOpen(!isOpen)
    }
  }

  const handleStatusChange = async (newStatus) => {
    if (newStatus === status) {
      setIsOpen(false)
      return
    }
    
    setIsSaving(true)
    setError(null)
    
    try {
      console.log(`Changing status from ${status} to ${newStatus} for result ${resultId} in search ${searchId}`)
      
      // First update local state for immediate UI feedback
      setStatus(newStatus)
      
      // Then save to the backend
      const resultIdString = String(resultId)
      
      const result = await SupabaseService.saveBacklinkStatus(
        user.id,
        searchId,
        resultIdString,
        newStatus
      )
      
      console.log("Status update result:", result)
      
      if (onStatusChange) {
        onStatusChange(newStatus)
      }
    } catch (error) {
      console.error('Failed to update status:', error)
      setError('Failed to update status')
    } finally {
      setIsSaving(false)
      setIsOpen(false)
    }
  }

  return (
    <div className="relative">
      {error && (
        <div className="absolute right-0 bottom-full mb-2 text-xs text-red-600 bg-red-50 p-1 rounded shadow-sm">
          {error}
        </div>
      )}
      
      <button
        onClick={toggleDropdown}
        disabled={isSaving}
        className={`flex items-center justify-between rounded-md px-3 py-1.5 text-xs font-medium ${getStatusColor(
          status
        )} min-w-[100px] ${
          isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
        }`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span>{getStatusLabel(status)}</span>
        {isSaving ? (
          <SafeIcon icon={FiLoader} className="w-3 h-3 ml-1 animate-spin" />
        ) : (
          <SafeIcon icon={FiChevronDown} className="w-3 h-3 ml-1" />
        )}
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-1 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
          >
            <div className="py-1" role="menu">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleStatusChange(option.value)}
                  className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 ${
                    status === option.value ? 'font-medium' : 'font-normal'
                  }`}
                  role="menuitem"
                >
                  <span className={`w-3 h-3 rounded-full mr-2 ${option.color}`}></span>
                  {option.label}
                  {status === option.value && <SafeIcon icon={FiCheck} className="w-3 h-3 ml-auto" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default StatusDropdown
